import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { formatCurrency, dashboardAvanzadoAPI } from '../services/api';
import config from '../config';
import useIsMobile from '../hooks/useIsMobile';
import FiltrosAvanzados from './dashboard/FiltrosAvanzados';
import { VistaMaestraAdminFullscreen } from './dashboard/TablaMaestraAdmin';
import TablaLaboral from './dashboard/TablaLaboral';
import ExportadorReportes from './dashboard/ExportadorReportes';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import '../styles/dashboard-avanzado.css';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

/*
 * DashboardAvanzadoClaude
 * -----------------------
 * Rediseño estructural y visual del dashboard avanzado. Cambios clave:
 *  - Una sola pantalla unificada (sin modos excluyentes tabla/gráficos × laboral/financiero).
 *  - KPIs financieros Y laborales visibles simultáneamente.
 *  - Insights automáticos: Pareto de clientes, riesgo (vencidos sin abono),
 *    tipo más rezagado, persona más cargada.
 *  - Performance por tipo en bar horizontal apilado (% pagado vs % avance).
 *  - Vista NUEVA: "Carga por personal" agregada client-side desde la matriz laboral.
 *  - Tabs internos para tabla / laboral / personal sin perder KPIs ni insights.
 */

const PALETTE = {
  primary: '#6366F1',
  primaryDark: '#4F46E5',
  violet: '#8B5CF6',
  success: '#10B981',
  successSoft: 'rgba(16,185,129,0.12)',
  danger: '#EF4444',
  dangerSoft: 'rgba(239,68,68,0.12)',
  warning: '#F59E0B',
  warningSoft: 'rgba(245,158,11,0.12)',
  cyan: '#06B6D4',
  cyanSoft: 'rgba(6,182,212,0.12)',
  pink: '#EC4899',
  slate900: '#0F172A',
  slate700: '#334155',
  slate500: '#64748B',
  slate300: '#CBD5E1',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
};

const buildQueryParams = (filtros) => {
  const params = {};
  Object.entries(filtros || {}).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) return;
    if (Array.isArray(value)) {
      if (value.length > 0) params[key] = value.join(',');
    } else {
      params[key] = value;
    }
  });
  return params;
};

const filtrosPorDefecto = () => {
  const n = new Date();
  return {
    meses: [String(n.getMonth() + 1)],
    anio: String(n.getFullYear()),
    fechaDesde: '',
    fechaHasta: '',
    tipos: [],
    clienteId: '',
    montoMin: '',
    montoMax: '',
    estadoPago: [],
    estadoExpediente: []
  };
};

const fmtFechaPe = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const estadoParteLabel = (estado) => {
  const map = {
    PENDIENTE: 'Pendiente',
    EN_PROGRESO: 'En progreso',
    COMPLETADA: 'Completada',
  };
  return map[estado] || estado || '—';
};

/** Plazo respecto a fechaLimite; sortKey menor = más urgente. */
const calcPlazoParte = (fechaLimite, estado) => {
  if (estado === 'COMPLETADA') {
    return { etiqueta: 'Completada', tone: 'ok', sortKey: 10000 };
  }
  if (!fechaLimite) {
    return { etiqueta: 'Sin límite', tone: 'muted', sortKey: 5000 };
  }
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fl = new Date(fechaLimite);
  fl.setHours(0, 0, 0, 0);
  const dias = Math.round((fl - hoy) / 86400000);
  if (dias < 0) {
    return { etiqueta: `Vencida hace ${Math.abs(dias)} d`, tone: 'danger', sortKey: dias };
  }
  if (dias === 0) {
    return { etiqueta: 'Vence hoy', tone: 'warning', sortKey: -1 };
  }
  if (dias <= 7) {
    return { etiqueta: `Vence en ${dias} d`, tone: 'warning', sortKey: dias };
  }
  return { etiqueta: 'A tiempo', tone: 'ok', sortKey: dias + 100 };
};

const plazoBadgeStyle = (tone) => {
  if (tone === 'danger') {
    return { background: PALETTE.dangerSoft, color: '#B91C1C', border: '1px solid rgba(239,68,68,0.35)' };
  }
  if (tone === 'warning') {
    return { background: PALETTE.warningSoft, color: '#B45309', border: '1px solid rgba(245,158,11,0.35)' };
  }
  if (tone === 'ok') {
    return { background: PALETTE.successSoft, color: '#047857', border: '1px solid rgba(16,185,129,0.35)' };
  }
  return { background: PALETTE.slate100, color: PALETTE.slate700, border: `1px solid ${PALETTE.slate300}` };
};

const normNombre = (n) => String(n || '').trim().toLowerCase();

/** Fecha de entrega solo existe si la parte se marcó COMPLETADA (campo fechaCompletado en BD). */
const fmtEntrega = (row) => {
  if (row.estado === 'COMPLETADA') {
    return row.fechaCompletado ? fmtFechaPe(row.fechaCompletado) : 'Completada (sin fecha)';
  }
  return '—';
};

const StatPill = ({ value, label, tone = 'muted' }) => {
  const tones = {
    success: { bg: '#ECFDF5', color: '#047857', border: '#6EE7B7' },
    info: { bg: '#ECFEFF', color: '#0E7490', border: '#67E8F9' },
    danger: { bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
    warning: { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
    muted: { bg: '#F8FAFC', color: '#475569', border: '#E2E8F0' },
  };
  const s = tones[tone] || tones.muted;
  return (
    <div style={{
      background: '#fff',
      padding: '6px 10px',
      borderRadius: 8,
      border: `1px solid ${s.border}`,
      minWidth: 52,
    }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: s.color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 9, color: s.color, fontWeight: 700, letterSpacing: 0.3, opacity: 0.9 }}>{label}</div>
    </div>
  );
};

const recalcularRegistro = (base) => {
  const partes = base.partes || [];
  const expedientes = new Set(partes.map((p) => p.expedienteId));
  let completadas = 0;
  let enCurso = 0;
  let vencidas = 0;
  let proximas = 0;
  for (const p of partes) {
    if (p.celdaTipo === 'completado' || p.estado === 'COMPLETADA') {
      completadas += 1;
    } else if (p.celdaTipo === 'asignado') {
      enCurso += 1;
      if (p.plazo?.tone === 'danger') vencidas += 1;
      else if (p.plazo?.tone === 'warning' && p.plazo?.sortKey <= 7) proximas += 1;
    } else if (p.celdaTipo === 'sin_asignar') {
      if (p.plazo?.tone === 'danger') vencidas += 1;
      else if (p.plazo?.tone === 'warning' && p.plazo?.sortKey <= 7) proximas += 1;
    }
  }
  const total = partes.length;
  return {
    ...base,
    total,
    completadas,
    enCurso,
    vencidas,
    proximas,
    expedientes: expedientes.size,
    pctAvance: total > 0 ? Math.round((completadas / total) * 100) : 0,
  };
};

const TablaPartesDetalle = ({ rows, showResponsable }) => (
  <div style={{ overflowX: 'auto' }}>
    <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: 13 }}>
      <thead className="table-light sticky-top">
        <tr>
          {showResponsable && <th>Responsable</th>}
          <th>Expediente</th>
          <th>Cliente</th>
          <th>Tipo</th>
          <th>Parte</th>
          <th>Estado</th>
          <th>Plazo</th>
          <th>Últ. actualización</th>
          <th>Límite</th>
          <th title="Solo se registra al marcar la parte como Completada">Entrega</th>
          <th className="text-end">Acciones</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.parteId || `${row.expedienteId}-${row.descripcion}-${row.asignadoNombre || ''}`}>
            {showResponsable && (
              <td className="small fw-semibold">{row.asignadoNombre || 'Sin asignar'}</td>
            )}
            <td>
              <Link
                to={`/expedientes/${row.expedienteId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="fw-semibold text-decoration-none"
              >
                {row.expedienteNumero}
              </Link>
            </td>
            <td style={{ maxWidth: 120 }} className="text-truncate" title={row.clienteNombre}>
              {row.clienteNombre || '—'}
            </td>
            <td style={{ maxWidth: 100 }} className="small text-truncate" title={row.tipoNombre}>
              {row.tipoNombre || '—'}
            </td>
            <td>{row.descripcion}</td>
            <td>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 8px',
                  borderRadius: 6,
                  background: PALETTE.slate100,
                  color: PALETTE.slate700,
                  border: `1px solid ${PALETTE.slate300}`,
                }}
              >
                {estadoParteLabel(row.estado)}
              </span>
            </td>
            <td>
              <span
                style={{
                  ...plazoBadgeStyle(row.plazo?.tone),
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                }}
              >
                {row.plazo?.etiqueta}
              </span>
            </td>
            <td className="small text-muted">{fmtFechaPe(row.fechaAsignacion)}</td>
            <td className="small">{fmtFechaPe(row.fechaLimite)}</td>
            <td className="small">{fmtEntrega(row)}</td>
            <td className="text-end text-nowrap">
              <Link
                to={`/expedientes/${row.expedienteId}?tab=archivos&parte=${encodeURIComponent(
                  (row.descripcion || '').trim().replace(/\s+/g, '_')
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-success me-1"
                title="Ir a carpeta de la parte"
              >
                <i className="fas fa-folder-open" />
              </Link>
              <Link
                to={`/expedientes/${row.expedienteId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
                title="Abrir expediente"
              >
                <i className="fas fa-external-link-alt" />
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ModalCargaPersonalDetalle = ({ lista, onClose, isMobile }) => {
  const navItems = useMemo(() => {
    const items = [{ key: '__TODOS__', label: 'Todos', esSinAsignar: false, total: lista.reduce((s, p) => s + p.total, 0) }];
    const sin = lista.find((p) => p.esSinAsignar);
    if (sin) items.push({ key: sin.key, label: 'Sin asignar', esSinAsignar: true, total: sin.total, vencidas: sin.vencidas });
    const equipo = lista
      .filter((p) => !p.esSinAsignar)
      .sort((a, b) => normNombre(a.nombre).localeCompare(normNombre(b.nombre), 'es'));
    equipo.forEach((p) => {
      items.push({
        key: p.key,
        label: p.nombre,
        esSinAsignar: false,
        total: p.total,
        vencidas: p.vencidas,
      });
    });
    return items;
  }, [lista]);

  const [navKey, setNavKey] = useState(() => navItems[1]?.key || navItems[0]?.key || '__TODOS__');

  const personaActiva = useMemo(
    () => (navKey === '__TODOS__' ? null : lista.find((p) => p.key === navKey)),
    [navKey, lista]
  );

  const partesOrdenadas = useMemo(() => {
    const source =
      navKey === '__TODOS__'
        ? lista.flatMap((p) =>
            (p.partes || []).map((row) => ({
              ...row,
              asignadoNombre: p.esSinAsignar ? null : p.nombre,
            }))
          )
        : (personaActiva?.partes || []);
    return [...source].sort((a, b) => (a.plazo?.sortKey ?? 0) - (b.plazo?.sortKey ?? 0));
  }, [navKey, lista, personaActiva]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const titulo = navKey === '__TODOS__' ? 'Detalle de carga — Todos' : (personaActiva?.esSinAsignar ? 'Partes sin asignar' : personaActiva?.nombre);
  const resumen = personaActiva || {
    expedientes: new Set(partesOrdenadas.map((r) => r.expedienteId)).size,
    total: partesOrdenadas.length,
    completadas: partesOrdenadas.filter((r) => r.estado === 'COMPLETADA').length,
    enCurso: partesOrdenadas.filter((r) => r.estado !== 'COMPLETADA' && r.celdaTipo === 'asignado').length,
    vencidas: partesOrdenadas.filter((r) => r.plazo?.tone === 'danger').length,
    proximas: partesOrdenadas.filter((r) => r.plazo?.tone === 'warning' && r.plazo?.sortKey <= 7).length,
    pctAvance: 0,
  };
  if (resumen.total > 0) {
    resumen.pctAvance = Math.round((resumen.completadas / resumen.total) * 100);
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dac-detalle-personal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10050,
        background: 'rgba(15,23,42,0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 1200,
          maxHeight: 'min(92vh, 940px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(15,23,42,0.35)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${PALETTE.slate100}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <h5 id="dac-detalle-personal-title" style={{ margin: 0, fontSize: 17, fontWeight: 800, color: PALETTE.slate900 }}>
            Carga por personal — detalle
          </h5>
          <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0, flexDirection: isMobile ? 'column' : 'row' }}>
          <nav
            aria-label="Personal"
            style={{
              width: isMobile ? '100%' : 220,
              flexShrink: 0,
              borderRight: isMobile ? 'none' : `1px solid ${PALETTE.slate100}`,
              borderBottom: isMobile ? `1px solid ${PALETTE.slate100}` : 'none',
              background: PALETTE.slate50,
              overflowY: 'auto',
              maxHeight: isMobile ? 160 : 'none',
              padding: '8px 6px',
            }}
          >
            {navItems.map((item) => {
              const active = navKey === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setNavKey(item.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    marginBottom: 2,
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    color: item.esSinAsignar ? PALETTE.danger : (active ? PALETTE.primaryDark : PALETTE.slate700),
                    background: active ? '#fff' : 'transparent',
                    boxShadow: active ? '0 1px 4px rgba(15,23,42,0.08)' : 'none',
                  }}
                >
                  <span className="text-truncate" style={{ maxWidth: 150 }}>{item.label}</span>
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 999,
                    background: item.vencidas > 0 ? PALETTE.dangerSoft : PALETTE.slate100,
                    color: item.vencidas > 0 ? '#B91C1C' : PALETTE.slate500,
                    marginLeft: 6,
                  }}>
                    {item.total}
                  </span>
                </button>
              );
            })}
          </nav>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${PALETTE.slate100}` }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: PALETTE.slate900 }}>{titulo}</div>
              <p style={{ margin: '4px 0 8px', fontSize: 12, color: PALETTE.slate500 }}>
                {resumen.expedientes} expediente{resumen.expedientes === 1 ? '' : 's'} · {resumen.total} parte
                {resumen.total === 1 ? '' : 's'} · {resumen.pctAvance}% completadas
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <StatPill value={resumen.completadas} label="OK" tone="success" />
                {personaActiva && !personaActiva.esSinAsignar && (
                  <StatPill value={resumen.enCurso} label="EN CURSO" tone="info" />
                )}
                {resumen.vencidas > 0 && <StatPill value={resumen.vencidas} label="VENCIDAS" tone="danger" />}
                {resumen.proximas > 0 && <StatPill value={resumen.proximas} label="≤7 DÍAS" tone="warning" />}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: PALETTE.slate500 }}>
                «Últ. actualización» ≈ último cambio en la parte. «Entrega» solo si estado = Completada.
              </p>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {partesOrdenadas.length === 0 ? (
                <div className="text-center text-muted py-5">
                  {personaActiva && !personaActiva.esSinAsignar
                    ? 'Sin partes asignadas en el periodo filtrado.'
                    : 'Sin partes en el periodo filtrado.'}
                </div>
              ) : (
                <TablaPartesDetalle rows={partesOrdenadas} showResponsable={navKey === '__TODOS__'} />
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: '10px 16px', borderTop: `1px solid ${PALETTE.slate100}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Icon = ({ name, size = 18, color = 'currentColor' }) => {
  const c = {
    width: size, height: size, viewBox: '0 0 24 24',
    fill: 'none', stroke: color, strokeWidth: 1.8,
    strokeLinecap: 'round', strokeLinejoin: 'round'
  };
  switch (name) {
    case 'folder': return <svg {...c}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>;
    case 'wallet': return <svg {...c}><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /><circle cx="16.5" cy="14.5" r="1.2" fill={color} stroke="none" /></svg>;
    case 'check': return <svg {...c}><path d="m5 12 5 5 9-11" /></svg>;
    case 'alert': return <svg {...c}><path d="M12 3 2 21h20z" /><path d="M12 10v4" /><circle cx="12" cy="17.5" r="0.8" fill={color} stroke="none" /></svg>;
    case 'bolt': return <svg {...c}><path d="M13 2 4 14h7l-1 8 9-12h-7z" /></svg>;
    case 'users': return <svg {...c}><circle cx="9" cy="8" r="3.2" /><path d="M2.5 19a6.5 6.5 0 0 1 13 0" /><circle cx="17" cy="9" r="2.4" /><path d="M15.5 19a5 5 0 0 1 6-4.8" /></svg>;
    case 'trend': return <svg {...c}><path d="M3 17 9 11 13 15 21 7" /><path d="M14 7h7v7" /></svg>;
    case 'back': return <svg {...c}><path d="M19 12H5" /><path d="m12 5-7 7 7 7" /></svg>;
    case 'sparkle': return <svg {...c}><path d="M12 3v4" /><path d="M12 17v4" /><path d="M3 12h4" /><path d="M17 12h4" /><path d="m5.6 5.6 2.8 2.8" /><path d="m15.6 15.6 2.8 2.8" /><path d="m5.6 18.4 2.8-2.8" /><path d="m15.6 8.4 2.8-2.8" /></svg>;
    case 'refresh': return <svg {...c}><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" /><path d="M3 21v-5h5" /></svg>;
    case 'target': return <svg {...c}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" fill={color} stroke="none" /></svg>;
    case 'fire': return <svg {...c}><path d="M12 3c2 5-2 5 0 9 4-2 5-6 4-9 4 3 6 7 6 11a8 8 0 1 1-16 0c0-3 2-6 6-11" /></svg>;
    case 'crown': return <svg {...c}><path d="M3 8l4 5 5-8 5 8 4-5v11H3z" /></svg>;
    case 'table': return <svg {...c}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /><path d="M10 5v14" /></svg>;
    case 'grid': return <svg {...c}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>;
    default: return null;
  }
};

// Tarjeta KPI compacta y consistente
const KpiCard = ({ icon, label, value, sub, accent, progress }) => (
  <div className="dac-card dac-kpi" style={{
    background: '#fff', borderRadius: 16, padding: 16,
    border: '1px solid rgba(226,232,240,0.8)',
    boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)',
    position: 'relative', overflow: 'hidden'
  }}>
    <div style={{
      position: 'absolute', inset: 0,
      background: `linear-gradient(135deg, ${accent}10, transparent 60%)`,
      pointerEvents: 'none'
    }} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', boxShadow: `0 4px 12px ${accent}55`
      }}>
        <Icon name={icon} size={20} color="#fff" />
      </div>
      {sub && (
        <span style={{
          fontSize: 11, fontWeight: 600,
          color: PALETTE.slate500, background: PALETTE.slate100,
          padding: '3px 8px', borderRadius: 999
        }}>
          {sub}
        </span>
      )}
    </div>
    <div style={{ marginTop: 12, position: 'relative' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: PALETTE.slate500, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: PALETTE.slate900, letterSpacing: -0.3 }}>
        {value}
      </div>
      {typeof progress === 'number' && (
        <div style={{ marginTop: 10, height: 5, borderRadius: 999, background: PALETTE.slate100, overflow: 'hidden' }}>
          <div style={{
            width: `${Math.min(100, Math.max(0, progress))}%`, height: '100%',
            background: `linear-gradient(90deg, ${accent}, ${accent}aa)`, borderRadius: 999,
            transition: 'width 600ms ease'
          }} />
        </div>
      )}
    </div>
  </div>
);

// Card de insight con header + body
const InsightCard = ({ icon, accent, title, subtitle, children }) => (
  <div className="dac-card" style={{
    background: '#fff', borderRadius: 16, padding: 16,
    border: '1px solid rgba(226,232,240,0.8)',
    boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)',
    height: '100%', display: 'flex', flexDirection: 'column'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: `${accent}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon name={icon} size={18} color={accent} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: PALETTE.slate500 }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: PALETTE.slate500, marginTop: 1 }}>{subtitle}</div>
        )}
      </div>
    </div>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

const SectionTitle = ({ children, hint, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.slate500 }}>
        {children}
      </div>
      {hint && <div style={{ fontSize: 13, color: PALETTE.slate500, marginTop: 2 }}>{hint}</div>}
    </div>
    {right}
  </div>
);

const DashboardAvanzadoClaude = ({ onVolver }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [filtros, setFiltros] = useState(filtrosPorDefecto);
  const [datos, setDatos] = useState([]);
  const [totales, setTotales] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [laboralMatriz, setLaboralMatriz] = useState(null);
  const [totalesLaborales, setTotalesLaborales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLaboral, setLoadingLaboral] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('personal');
  const [detalleModalOpen, setDetalleModalOpen] = useState(false);
  const [tablaAdminOpen, setTablaAdminOpen] = useState(false);
  const [personalEquipo, setPersonalEquipo] = useState([]);

  const handleExpedienteActualizado = useCallback((actualizado) => {
    setDatos((prev) => prev.map((exp) => (exp.id === actualizado.id ? actualizado : exp)));
  }, []);

  useEffect(() => {
    if (!user || user.rol !== 'JEFE') {
      navigate('/expedientes', { replace: true });
    }
  }, [user, navigate]);

  // Reintento ligero ante fallos transitorios de red/backend (Error de conexión)
  const requestConReintento = useCallback(async (fn, intentos = 2) => {
    let ultimoErr;
    for (let i = 0; i <= intentos; i += 1) {
      try {
        return await fn();
      } catch (err) {
        ultimoErr = err;
        const msg = err?.message || '';
        const reintentable = msg.includes('conexión') || msg.includes('temporal') || msg.includes('timeout');
        if (!reintentable || i === intentos) throw err;
        await new Promise(r => setTimeout(r, 400 * (i + 1)));
      }
    }
    throw ultimoErr;
  }, []);

  /*
   * Cargamos las 3 fuentes en SERIE para no presionar al backend con 3 requests
   * simultáneas al mount. Cada una con reintento ligero. Si una falla, las demás
   * siguen e informamos en consola pero no rompemos toda la vista.
   */
  const cargarTodo = useCallback(async () => {
    if (!user || user.rol !== 'JEFE') return;
    setLoading(true);
    setLoadingLaboral(true);
    setError(null);
    const params = buildQueryParams(filtros);
    let huboError = false;

    try {
      const datosRes = await requestConReintento(() => dashboardAvanzadoAPI.getDatos(params));
      setDatos(datosRes.data.expedientes);
      setTotales(datosRes.data.totales);
    } catch (err) {
      console.error('Error cargando datos del dashboard avanzado:', err);
      huboError = true;
    }

    try {
      const estRes = await requestConReintento(() => dashboardAvanzadoAPI.getEstadisticas(params));
      setEstadisticas(estRes.data);
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
      huboError = true;
    }
    setLoading(false);

    try {
      const labRes = await requestConReintento(() => dashboardAvanzadoAPI.getDatosLaboralesMatriz(params));
      setLaboralMatriz({ porTipo: labRes.data.porTipo || [] });
      setTotalesLaborales(labRes.data.totales || null);
    } catch (err) {
      console.error('Error cargando matriz laboral:', err);
      setLaboralMatriz(null);
      setTotalesLaborales(null);
      huboError = true;
    } finally {
      setLoadingLaboral(false);
    }

    try {
      const persRes = await requestConReintento(() => api.get('/user', { params: { rol: 'PERSONAL' } }));
      const raw = persRes.data;
      const lista = Array.isArray(raw) ? raw : (raw?.usuarios || raw?.data || []);
      setPersonalEquipo(lista);
    } catch (err) {
      console.error('Error cargando personal del equipo:', err);
      setPersonalEquipo([]);
    }

    if (huboError) {
      setError('Algunas secciones no se pudieron cargar. El servidor puede estar congestionado, intenta actualizar.');
    }
  }, [filtros, user, requestConReintento]);

  // Carga en serie (datos → estadísticas → matriz laboral) en el efecto principal.
  // No hay cargarLaboral separado: la matriz se incluye dentro de cargarTodo.
  useEffect(() => { cargarTodo(); }, [cargarTodo]);

  // ── Cálculos derivados ────────────────────────────────────────────────

  // Pareto: ¿cuántos clientes concentran el 80% del monto contratado?
  const pareto = useMemo(() => {
    if (!estadisticas?.topClientes?.length) return null;
    const ordenados = [...estadisticas.topClientes].sort((a, b) => b.montoTotal - a.montoTotal);
    const total = ordenados.reduce((s, c) => s + (Number(c.montoTotal) || 0), 0);
    if (total <= 0) return null;
    let acumulado = 0;
    let n = 0;
    for (const c of ordenados) {
      acumulado += Number(c.montoTotal) || 0;
      n += 1;
      if (acumulado / total >= 0.8) break;
    }
    return {
      n,
      totalClientes: ordenados.length,
      pctMonto: Math.round((acumulado / total) * 100),
      lider: ordenados[0],
    };
  }, [estadisticas]);

  // Tipo más rezagado (peor combinación de % pagado + % avance)
  const tipoRezagado = useMemo(() => {
    if (!estadisticas?.porTipo) return null;
    const arr = Object.entries(estadisticas.porTipo).map(([nombre, t]) => {
      const pctPagado = t.montoTotal > 0 ? (t.abonado / t.montoTotal) * 100 : 0;
      const pctAvance = t.partes > 0 ? (t.partesCompletadas / t.partes) * 100 : 0;
      return {
        nombre,
        cantidad: t.cantidad,
        montoTotal: t.montoTotal,
        adeudado: t.adeudado,
        pctPagado, pctAvance,
        score: pctPagado * 0.5 + pctAvance * 0.5
      };
    }).filter(t => t.cantidad > 0);
    if (arr.length === 0) return null;
    arr.sort((a, b) => a.score - b.score);
    return arr[0];
  }, [estadisticas]);

  // Riesgo: vencidos sin abonos (o con muy poco)
  const expedientesEnRiesgo = useMemo(() => {
    if (!datos?.length) return [];
    return datos
      .filter(e => e.estadoExpediente === 'VENCIDO' && (e.porcentajePagado || 0) < 30)
      .sort((a, b) => (b.adeudado || 0) - (a.adeudado || 0))
      .slice(0, 5);
  }, [datos]);

  // Carga por personal: matriz laboral + todo el equipo (incluye quienes tienen 0 partes en el periodo)
  const cargaPersonal = useMemo(() => {
    const mapa = new Map();
    if (laboralMatriz?.porTipo?.length) {
      for (const grupo of laboralMatriz.porTipo) {
        for (const fila of grupo.filas || []) {
          for (const col of Object.keys(fila.celdas || {})) {
            const celda = fila.celdas[col];
            if (!celda || celda.tipo === 'vacío') continue;
            const detalle = celda.detalle || {};
            const persona = celda.tipo === 'sin_asignar'
              ? '__SIN_ASIGNAR__'
              : (detalle.asignadoNombre || '__SIN_ASIGNAR__');
            if (!mapa.has(persona)) {
              mapa.set(persona, {
                nombre: persona,
                esSinAsignar: persona === '__SIN_ASIGNAR__',
                expedientes: new Set(),
                partes: [],
              });
            }
            const reg = mapa.get(persona);
            reg.expedientes.add(detalle.expedienteId);
            const plazo = calcPlazoParte(detalle.fechaLimite, detalle.estado);
            reg.partes.push({
              parteId: detalle.parteId,
              descripcion: detalle.descripcion,
              estado: detalle.estado,
              expedienteNumero: detalle.expedienteNumero,
              expedienteId: detalle.expedienteId,
              clienteNombre: fila.clienteNombreCompleto || '',
              tipoNombre: grupo.tipoNombre || '',
              asignadoNombre: detalle.asignadoNombre,
              fechaAsignacion: detalle.fechaAsignacion,
              fechaLimite: detalle.fechaLimite,
              fechaCompletado: detalle.fechaCompletado,
              celdaTipo: celda.tipo,
              plazo,
            });
          }
        }
      }
    }

    const fromMatriz = Array.from(mapa.values()).map((r) =>
      recalcularRegistro({
        key: r.esSinAsignar ? 'sin_asignar' : `name_${normNombre(r.nombre)}`,
        nombre: r.esSinAsignar ? 'Sin asignar' : r.nombre,
        esSinAsignar: r.esSinAsignar,
        usuarioId: null,
        partes: r.partes,
      })
    );

    const byNorm = new Map();
    let sinAsignar = null;
    for (const r of fromMatriz) {
      if (r.esSinAsignar) sinAsignar = r;
      else byNorm.set(normNombre(r.nombre), r);
    }

    const merged = [];
    if (sinAsignar) merged.push(sinAsignar);

    const equipo = [...personalEquipo].sort((a, b) =>
      normNombre(a.nombre).localeCompare(normNombre(b.nombre), 'es')
    );
    for (const u of equipo) {
      const n = normNombre(u.nombre);
      const exist = byNorm.get(n);
      if (exist) {
        merged.push({ ...exist, key: `u_${u.id}`, usuarioId: u.id, nombre: u.nombre });
        byNorm.delete(n);
      } else {
        merged.push(
          recalcularRegistro({
            key: `u_${u.id}`,
            nombre: u.nombre,
            esSinAsignar: false,
            usuarioId: u.id,
            partes: [],
          })
        );
      }
    }
    byNorm.forEach((r) => merged.push(r));

    return merged.sort((a, b) => {
      if (a.esSinAsignar) return 1;
      if (b.esSinAsignar) return -1;
      return b.enCurso - a.enCurso || normNombre(a.nombre).localeCompare(normNombre(b.nombre), 'es');
    });
  }, [laboralMatriz, personalEquipo]);

  const personaMasCargada = useMemo(() => {
    const reales = cargaPersonal.filter(p => !p.esSinAsignar);
    if (reales.length === 0) return null;
    return [...reales].sort((a, b) => b.enCurso - a.enCurso)[0];
  }, [cargaPersonal]);

  // Performance por tipo: bar horizontal con dos métricas
  const performancePorTipo = useMemo(() => {
    if (!estadisticas?.porTipo) return null;
    const labels = Object.keys(estadisticas.porTipo);
    const valores = labels.map(nombre => estadisticas.porTipo[nombre]);
    return {
      labels,
      pctPagado: valores.map(t => t.montoTotal > 0 ? Math.round((t.abonado / t.montoTotal) * 100) : 0),
      pctAvance: valores.map(t => t.partes > 0 ? Math.round((t.partesCompletadas / t.partes) * 100) : 0),
      cantidades: valores.map(t => t.cantidad),
    };
  }, [estadisticas]);

  const barData = useMemo(() => {
    if (!performancePorTipo) return null;
    return {
      labels: performancePorTipo.labels,
      datasets: [
        {
          label: '% Pagado',
          data: performancePorTipo.pctPagado,
          backgroundColor: PALETTE.success,
          borderRadius: 6,
          barPercentage: 0.65,
          categoryPercentage: 0.7,
        },
        {
          label: '% Avance partes',
          data: performancePorTipo.pctAvance,
          backgroundColor: PALETTE.primary,
          borderRadius: 6,
          barPercentage: 0.65,
          categoryPercentage: 0.7,
        }
      ]
    };
  }, [performancePorTipo]);

  const barOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 10, font: { size: 11 } } },
      tooltip: {
        backgroundColor: PALETTE.slate900, cornerRadius: 8, padding: 10,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x}%`,
          afterLabel: (ctx) => {
            const i = ctx.dataIndex;
            if (!performancePorTipo) return '';
            return `Expedientes: ${performancePorTipo.cantidades[i]}`;
          }
        }
      }
    },
    scales: {
      x: {
        min: 0, max: 100,
        ticks: { color: PALETTE.slate500, callback: v => v + '%', font: { size: 11 } },
        grid: { color: 'rgba(148,163,184,0.18)' }
      },
      y: {
        ticks: { color: PALETTE.slate700, font: { size: 12, weight: '600' } },
        grid: { display: false }
      }
    }
  }), [performancePorTipo]);

  // Tendencia mensual: creaciones + contratado + abonado
  const tendenciaData = useMemo(() => {
    if (!estadisticas?.porMes) return null;
    const meses = Object.keys(estadisticas.porMes).sort();
    if (meses.length === 0) return null;
    const labels = meses.map(m => {
      const [y, mm] = m.split('-');
      const names = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
      return `${names[parseInt(mm,10)-1]} ${y.slice(2)}`;
    });
    return {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Contratado',
          data: meses.map(m => estadisticas.porMes[m].montoTotal),
          borderColor: PALETTE.primary,
          backgroundColor: 'rgba(99,102,241,0.15)',
          fill: true, tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 3, pointBackgroundColor: '#fff', pointBorderColor: PALETTE.primary,
          yAxisID: 'y',
        },
        {
          type: 'line',
          label: 'Abonado',
          data: meses.map(m => estadisticas.porMes[m].abonado),
          borderColor: PALETTE.success,
          backgroundColor: 'rgba(16,185,129,0.08)',
          fill: false, tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 3, pointBackgroundColor: '#fff', pointBorderColor: PALETTE.success,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'Expedientes creados',
          data: meses.map(m => estadisticas.porMes[m].cantidad),
          backgroundColor: 'rgba(139,92,246,0.6)',
          borderRadius: 6,
          yAxisID: 'y1',
          barPercentage: 0.4,
        }
      ]
    };
  }, [estadisticas]);

  const tendenciaOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 10, font: { size: 11 } } },
      tooltip: {
        backgroundColor: PALETTE.slate900, cornerRadius: 8, padding: 10,
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed.y;
            if (ctx.dataset.label === 'Expedientes creados') {
              return `${ctx.dataset.label}: ${v}`;
            }
            return `${ctx.dataset.label}: S/ ${Number(v).toLocaleString('es-PE')}`;
          }
        }
      }
    },
    scales: {
      y: {
        position: 'left', beginAtZero: true,
        grid: { color: 'rgba(148,163,184,0.18)' },
        ticks: {
          color: PALETTE.slate500, font: { size: 11 },
          callback: v => 'S/ ' + Number(v).toLocaleString('es-PE')
        }
      },
      y1: {
        position: 'right', beginAtZero: true,
        grid: { display: false },
        ticks: { color: PALETTE.slate500, font: { size: 11 }, precision: 0 }
      },
      x: { grid: { display: false }, ticks: { color: PALETTE.slate500, font: { size: 11 } } }
    }
  }), []);

  // Top deudores
  const topDeudores = useMemo(() => {
    if (!estadisticas?.topClientes) return [];
    return [...estadisticas.topClientes]
      .filter(c => (c.adeudado || 0) > 0)
      .sort((a, b) => (b.adeudado || 0) - (a.adeudado || 0))
      .slice(0, 5);
  }, [estadisticas]);

  const topPorMonto = useMemo(() => {
    if (!estadisticas?.topClientes) return [];
    return [...estadisticas.topClientes].slice(0, 5);
  }, [estadisticas]);

  if (!user || user.rol !== 'JEFE') return null;

  if (loading && !datos.length) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%',
          border: '4px solid rgba(99,102,241,0.2)',
          borderTopColor: PALETTE.primary,
          animation: 'dacSpin 800ms linear infinite'
        }} />
        <style>{`@keyframes dacSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Nota: el banner de error ahora se muestra como aviso suave dentro del dashboard,
  // no bloquea toda la pantalla (puede que parte de los datos sí se hayan cargado).

  return (
    <div className="dac-root" style={{
      background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)',
      minHeight: '100%', padding: '20px 16px 60px'
    }}>
      <style>{`
        .dac-root .dac-card { transition: transform 250ms ease, box-shadow 250ms ease; }
        .dac-root .dac-kpi:hover { transform: translateY(-3px); box-shadow: 0 2px 4px rgba(15,23,42,0.08), 0 16px 32px rgba(15,23,42,0.10) !important; }
        .dac-grid { display: grid; gap: 14px; }
        .dac-fade { animation: dacFadeIn 480ms ease both; }
        @keyframes dacFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .dac-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 14px; border-radius: 10px;
          font-size: 13px; font-weight: 600;
          border: 1px solid transparent; cursor: pointer;
          transition: all 180ms ease;
        }
        .dac-btn-ghost { background: rgba(255,255,255,0.7); color: #334155; border-color: rgba(203,213,225,0.7); backdrop-filter: blur(6px); }
        .dac-btn-ghost:hover { background: #fff; }
        .dac-tab {
          background: transparent; border: none; cursor: pointer;
          padding: 10px 16px; border-radius: 10px;
          font-size: 13px; font-weight: 600;
          color: #64748B; transition: all 180ms ease;
          display: inline-flex; align-items: center; gap: 8px;
        }
        .dac-tab:hover { background: rgba(99,102,241,0.06); color: #334155; }
        .dac-tab--active {
          background: #fff;
          color: #4F46E5;
          box-shadow: 0 2px 8px rgba(15,23,42,0.08);
        }
        @media (max-width: 1180px) { .dac-kpis { grid-template-columns: repeat(3,1fr) !important; } .dac-insights { grid-template-columns: repeat(2,1fr) !important; } .dac-mains { grid-template-columns: 1fr !important; } .dac-tops { grid-template-columns: 1fr !important; } }
        @media (max-width: 720px) { .dac-kpis { grid-template-columns: repeat(2,1fr) !important; } .dac-insights { grid-template-columns: 1fr !important; } }
        @media (max-width: 460px) { .dac-kpis { grid-template-columns: 1fr !important; } }
      `}</style>

      {/* HERO */}
      <div className="dac-fade" style={{
        position: 'relative',
        borderRadius: 22,
        padding: '22px 26px',
        background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 50%, #8B5CF6 100%)',
        color: '#fff', overflow: 'hidden', marginBottom: 18,
        boxShadow: '0 20px 50px rgba(79,70,229,0.32)'
      }}>
        <div style={{ position: 'absolute', right: -80, top: -80, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.22), transparent 70%)' }} />
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.18)',
              padding: '6px 12px', borderRadius: 999,
              fontSize: 12, fontWeight: 600, letterSpacing: 0.5,
              marginBottom: 12, backdropFilter: 'blur(6px)'
            }}>
              <Icon name="sparkle" size={14} /> Dashboard avanzado
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.15 }}>
              Vista ejecutiva unificada
            </h1>
            <p style={{ margin: '6px 0 0', opacity: 0.92, fontSize: 13 }}>
              Finanzas, operación y carga laboral juntos. Insights generados automáticamente.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="dac-btn dac-btn-ghost" onClick={() => cargarTodo()}>
              <Icon name="refresh" size={16} /> Actualizar
            </button>
            <ExportadorReportes filtros={filtros} />
            {onVolver && (
              <button
                className="dac-btn"
                onClick={onVolver}
                style={{ background: '#fff', color: PALETTE.primaryDark, fontWeight: 700 }}
              >
                <Icon name="back" size={16} /> Volver al clásico
              </button>
            )}
          </div>
        </div>
      </div>

      {/* FILTROS */}
      <div className="dac-fade">
        <FiltrosAvanzados onFilterChange={setFiltros} filtrosActuales={filtros} />
      </div>

      {/* KPIs UNIFICADOS */}
      <div className="dac-fade dac-grid dac-kpis" style={{
        gridTemplateColumns: 'repeat(6, 1fr)', marginBottom: 16
      }}>
        <KpiCard icon="folder" accent={PALETTE.primary} label="Expedientes"
          value={totales?.expedientes ?? 0}
          sub={totales?.partes != null ? `${totales.partes} partes` : null} />
        <KpiCard icon="wallet" accent={PALETTE.violet} label="Contratado"
          value={formatCurrency(totales?.montoTotal || 0)} />
        <KpiCard icon="check" accent={PALETTE.success} label="Abonado"
          value={formatCurrency(totales?.totalAbonado || 0)}
          sub={totales?.porcentajePagado != null ? `${Math.round(totales.porcentajePagado)}% pagado` : null}
          progress={totales?.porcentajePagado} />
        <KpiCard icon="alert" accent={PALETTE.danger} label="Adeudado"
          value={formatCurrency(totales?.totalAdeudado || 0)} />
        <KpiCard icon="trend" accent={PALETTE.cyan} label="Avance partes"
          value={`${Math.round(totales?.porcentajeAvance || 0)}%`}
          sub={totales?.partesCompletadas != null ? `${totales.partesCompletadas} ok` : null}
          progress={totales?.porcentajeAvance} />
        <KpiCard icon="bolt" accent={PALETTE.warning} label="Sin asignar"
          value={totalesLaborales?.sinAsignar ?? '—'}
          sub={totalesLaborales?.partes ? `de ${totalesLaborales.partes}` : null} />
      </div>

      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
          background: PALETTE.warningSoft,
          border: `1px solid ${PALETTE.warning}55`,
          borderRadius: 12, padding: '10px 14px',
          marginBottom: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: PALETTE.slate900 }}>
            <Icon name="alert" size={18} color={PALETTE.warning} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>{error}</span>
          </div>
          <button className="dac-btn dac-btn-ghost" onClick={() => cargarTodo()}>
            <Icon name="refresh" size={14} /> Reintentar
          </button>
        </div>
      )}

      {/* INSIGHTS AUTOMÁTICOS */}
      <SectionTitle hint="Detectados automáticamente a partir de los filtros aplicados">
        Insights ejecutivos
      </SectionTitle>
      <div className="dac-fade dac-grid dac-insights" style={{
        gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 18
      }}>
        {/* Pareto */}
        <InsightCard
          icon="target" accent={PALETTE.primary}
          title="Concentración (Pareto)"
          subtitle="¿Cuánto pesan tus mejores clientes?"
        >
          {pareto ? (
            <>
              <div style={{ fontSize: 30, fontWeight: 800, color: PALETTE.slate900, lineHeight: 1.1 }}>
                {pareto.n}
                <span style={{ fontSize: 14, fontWeight: 600, color: PALETTE.slate500, marginLeft: 6 }}>
                  / {pareto.totalClientes}
                </span>
              </div>
              <div style={{ fontSize: 12, color: PALETTE.slate500, marginTop: 4 }}>
                clientes concentran el <strong style={{ color: PALETTE.primaryDark }}>{pareto.pctMonto}%</strong> del monto contratado
              </div>
              {pareto.lider && (
                <div style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 10,
                  background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)'
                }}>
                  <div style={{ fontSize: 11, color: PALETTE.slate500, fontWeight: 600 }}>TOP 1</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: PALETTE.slate900 }}>{pareto.lider.nombre}</div>
                  <div style={{ fontSize: 12, color: PALETTE.slate500 }}>{formatCurrency(pareto.lider.montoTotal)}</div>
                </div>
              )}
            </>
          ) : <div style={{ fontSize: 13, color: PALETTE.slate500 }}>Sin datos suficientes.</div>}
        </InsightCard>

        {/* Riesgo */}
        <InsightCard
          icon="fire" accent={PALETTE.danger}
          title="Riesgo de cobranza"
          subtitle="Vencidos con poco o nada pagado"
        >
          {expedientesEnRiesgo.length > 0 ? (
            <>
              <div style={{ fontSize: 30, fontWeight: 800, color: PALETTE.danger, lineHeight: 1.1 }}>
                {expedientesEnRiesgo.length}
              </div>
              <div style={{ fontSize: 12, color: PALETTE.slate500, marginTop: 4 }}>
                expedientes vencidos con menos del 30% pagado
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {expedientesEnRiesgo.slice(0, 3).map(e => (
                  <button key={e.id}
                    onClick={() => navigate(`/expedientes/${e.id}`)}
                    style={{
                      textAlign: 'left', padding: '6px 10px', borderRadius: 8,
                      border: '1px solid rgba(239,68,68,0.18)',
                      background: 'rgba(239,68,68,0.05)',
                      cursor: 'pointer', display: 'block'
                    }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: PALETTE.slate900 }}>
                      {e.numero} · {e.cliente?.nombre || '—'}
                    </div>
                    <div style={{ fontSize: 11, color: PALETTE.danger, fontWeight: 600 }}>
                      Adeudado: {formatCurrency(e.adeudado)}
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: PALETTE.success }}>
              <Icon name="check" size={20} color={PALETTE.success} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Sin riesgo crítico detectado</span>
            </div>
          )}
        </InsightCard>

        {/* Tipo rezagado */}
        <InsightCard
          icon="bolt" accent={PALETTE.warning}
          title="Tipo más rezagado"
          subtitle="Combina cobro + avance de partes"
        >
          {tipoRezagado ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 800, color: PALETTE.slate900, lineHeight: 1.2 }}>
                {tipoRezagado.nombre}
              </div>
              <div style={{ fontSize: 12, color: PALETTE.slate500, marginTop: 2 }}>
                {tipoRezagado.cantidad} expediente{tipoRezagado.cantidad === 1 ? '' : 's'}
              </div>
              <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 11, color: PALETTE.slate500, fontWeight: 600 }}>% pagado</div>
                  <div style={{ height: 6, background: PALETTE.slate100, borderRadius: 999, overflow: 'hidden', marginTop: 3 }}>
                    <div style={{
                      width: `${Math.min(100, tipoRezagado.pctPagado)}%`, height: '100%',
                      background: PALETTE.success, borderRadius: 999
                    }} />
                  </div>
                  <div style={{ fontSize: 11, color: PALETTE.slate700, fontWeight: 700, marginTop: 2 }}>
                    {Math.round(tipoRezagado.pctPagado)}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: PALETTE.slate500, fontWeight: 600 }}>% avance partes</div>
                  <div style={{ height: 6, background: PALETTE.slate100, borderRadius: 999, overflow: 'hidden', marginTop: 3 }}>
                    <div style={{
                      width: `${Math.min(100, tipoRezagado.pctAvance)}%`, height: '100%',
                      background: PALETTE.primary, borderRadius: 999
                    }} />
                  </div>
                  <div style={{ fontSize: 11, color: PALETTE.slate700, fontWeight: 700, marginTop: 2 }}>
                    {Math.round(tipoRezagado.pctAvance)}%
                  </div>
                </div>
              </div>
            </>
          ) : <div style={{ fontSize: 13, color: PALETTE.slate500 }}>Sin datos.</div>}
        </InsightCard>

        {/* Persona más cargada */}
        <InsightCard
          icon="users" accent={PALETTE.cyan}
          title="Carga operativa"
          subtitle="Persona con más partes en curso"
        >
          {personaMasCargada ? (
            <>
              <div style={{ fontSize: 18, fontWeight: 800, color: PALETTE.slate900, lineHeight: 1.2 }}>
                {personaMasCargada.nombre}
              </div>
              <div style={{ fontSize: 12, color: PALETTE.slate500, marginTop: 2 }}>
                {personaMasCargada.expedientes} expediente{personaMasCargada.expedientes === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <div style={{ background: PALETTE.cyanSoft, padding: '6px 10px', borderRadius: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: PALETTE.cyan, lineHeight: 1 }}>
                    {personaMasCargada.enCurso}
                  </div>
                  <div style={{ fontSize: 10, color: PALETTE.slate500, fontWeight: 600 }}>EN CURSO</div>
                </div>
                {personaMasCargada.vencidas > 0 && (
                  <div style={{ background: PALETTE.dangerSoft, padding: '6px 10px', borderRadius: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: PALETTE.danger, lineHeight: 1 }}>
                      {personaMasCargada.vencidas}
                    </div>
                    <div style={{ fontSize: 10, color: PALETTE.slate500, fontWeight: 600 }}>VENCIDAS</div>
                  </div>
                )}
                {personaMasCargada.proximas > 0 && (
                  <div style={{ background: PALETTE.warningSoft, padding: '6px 10px', borderRadius: 8 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: PALETTE.warning, lineHeight: 1 }}>
                      {personaMasCargada.proximas}
                    </div>
                    <div style={{ fontSize: 10, color: PALETTE.slate500, fontWeight: 600 }}>≤ 7 DÍAS</div>
                  </div>
                )}
              </div>
            </>
          ) : <div style={{ fontSize: 13, color: PALETTE.slate500 }}>Sin partes asignadas en el periodo.</div>}
        </InsightCard>
      </div>

      {/* PERFORMANCE POR TIPO + TENDENCIA */}
      <div className="dac-fade dac-grid dac-mains" style={{
        gridTemplateColumns: '1fr 1.4fr', marginBottom: 18
      }}>
        <div className="dac-card" style={{
          background: '#fff', borderRadius: 16, padding: 18,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <SectionTitle hint="Cobro y avance por tipo de expediente">
            Performance por tipo
          </SectionTitle>
          <div style={{
            height: barData ? Math.max(220, (barData.labels.length * 56)) : 220
          }}>
            {barData && barData.labels.length > 0 ? (
              <Bar data={barData} options={barOptions} />
            ) : (
              <div style={{ textAlign: 'center', color: PALETTE.slate500, paddingTop: 40 }}>
                Sin datos para mostrar
              </div>
            )}
          </div>
        </div>

        <div className="dac-card" style={{
          background: '#fff', borderRadius: 16, padding: 18,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <SectionTitle hint="Contratado vs abonado y altas mensuales">
            Tendencia mensual
          </SectionTitle>
          <div style={{ height: 280 }}>
            {tendenciaData ? (
              <Line data={tendenciaData} options={tendenciaOptions} />
            ) : (
              <div style={{ textAlign: 'center', color: PALETTE.slate500, paddingTop: 40 }}>
                Sin datos para mostrar
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TOP CLIENTES */}
      <div className="dac-fade dac-grid dac-tops" style={{
        gridTemplateColumns: '1fr 1fr', marginBottom: 18
      }}>
        <div className="dac-card" style={{
          background: '#fff', borderRadius: 16, padding: 18,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <SectionTitle hint="Por monto contratado">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="crown" size={14} color={PALETTE.warning} /> Top 5 clientes
            </span>
          </SectionTitle>
          {topPorMonto.length === 0 ? (
            <div style={{ color: PALETTE.slate500, fontSize: 13, paddingTop: 16 }}>Sin clientes en el periodo.</div>
          ) : (
            <div>
              {topPorMonto.map((c, i) => {
                const pct = c.montoTotal > 0 ? (c.abonado / c.montoTotal) * 100 : 0;
                return (
                  <div key={c.id} style={{
                    display: 'grid', gridTemplateColumns: '28px 1fr auto',
                    gap: 12, padding: '10px 0',
                    borderBottom: i < topPorMonto.length - 1 ? `1px dashed ${PALETTE.slate100}` : 'none',
                    alignItems: 'center'
                  }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: i === 0 ? PALETTE.warning : PALETTE.slate100,
                      color: i === 0 ? '#fff' : PALETTE.slate700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: 13
                    }}>{i + 1}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.slate900,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>{c.nombre}</div>
                      <div style={{ fontSize: 12, color: PALETTE.slate500 }}>
                        {c.expedientes} expediente{c.expedientes === 1 ? '' : 's'} · {Math.round(pct)}% pagado
                      </div>
                      <div style={{ height: 4, background: PALETTE.slate100, borderRadius: 999, overflow: 'hidden', marginTop: 4 }}>
                        <div style={{
                          width: `${Math.min(100, pct)}%`, height: '100%',
                          background: pct >= 100 ? PALETTE.success : pct > 0 ? PALETTE.warning : PALETTE.danger
                        }} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: PALETTE.slate900 }}>
                        {formatCurrency(c.montoTotal)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dac-card" style={{
          background: '#fff', borderRadius: 16, padding: 18,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)'
        }}>
          <SectionTitle hint="Cobranza prioritaria">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="alert" size={14} color={PALETTE.danger} /> Top 5 deudores
            </span>
          </SectionTitle>
          {topDeudores.length === 0 ? (
            <div style={{
              padding: '24px 0', textAlign: 'center', color: PALETTE.success
            }}>
              <Icon name="check" size={28} color={PALETTE.success} />
              <div style={{ marginTop: 4, fontWeight: 600, fontSize: 13 }}>Sin deudas en el periodo</div>
            </div>
          ) : (
            <div>
              {topDeudores.map((c, i) => (
                <div key={c.id} style={{
                  display: 'grid', gridTemplateColumns: '28px 1fr auto',
                  gap: 12, padding: '10px 0',
                  borderBottom: i < topDeudores.length - 1 ? `1px dashed ${PALETTE.slate100}` : 'none',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: PALETTE.dangerSoft, color: PALETTE.danger,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 13
                  }}>{i + 1}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.slate900,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                    }}>{c.nombre}</div>
                    <div style={{ fontSize: 12, color: PALETTE.slate500 }}>
                      {c.expedientes} expediente{c.expedientes === 1 ? '' : 's'} · {formatCurrency(c.abonado)} pagado
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: PALETTE.danger }}>
                      {formatCurrency(c.adeudado)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* TABS DE VISTA DETALLADA */}
      <div className="dac-fade dac-card" style={{
        background: '#fff', borderRadius: 16, padding: 16,
        border: '1px solid rgba(226,232,240,0.8)',
        boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.05)',
        marginBottom: 18
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: 4, background: PALETTE.slate50,
          borderRadius: 12, marginBottom: 14, flexWrap: 'wrap'
        }}>
          <button className={`dac-tab ${tab === 'personal' ? 'dac-tab--active' : ''}`}
            onClick={() => setTab('personal')}>
            <Icon name="users" size={16} /> Carga por personal
            {cargaPersonal.length > 0 && (
              <span style={{
                fontSize: 11, padding: '2px 7px', borderRadius: 999,
                background: tab === 'personal' ? PALETTE.primary : PALETTE.slate300,
                color: tab === 'personal' ? '#fff' : PALETTE.slate700,
                fontWeight: 700
              }}>{cargaPersonal.length}</span>
            )}
          </button>
          <button className={`dac-tab ${tab === 'laboral' ? 'dac-tab--active' : ''}`}
            onClick={() => setTab('laboral')}>
            <Icon name="grid" size={16} /> Matriz laboral
          </button>
        </div>

        {tab === 'personal' && (
          loadingLaboral ? (
            <div style={{ textAlign: 'center', padding: 32, color: PALETTE.slate500 }}>
              Cargando carga por personal...
            </div>
          ) : cargaPersonal.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: PALETTE.slate500 }}>
              No hay personal ni partes en el periodo filtrado.
            </div>
          ) : (
            <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setDetalleModalOpen(true)}
              >
                <i className="fas fa-list-ul me-1" />
                Ver detallado
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
              {cargaPersonal.map(p => {
                const isSinAsignar = p.esSinAsignar;
                return (
                  <div key={p.key || p.nombre} style={{
                    background: isSinAsignar ? PALETTE.dangerSoft : PALETTE.slate50,
                    border: `1px solid ${isSinAsignar ? 'rgba(239,68,68,0.25)' : PALETTE.slate100}`,
                    borderRadius: 14, padding: 14
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div>
                        <div style={{
                          fontSize: 14, fontWeight: 700,
                          color: isSinAsignar ? PALETTE.danger : PALETTE.slate900,
                          display: 'flex', alignItems: 'center', gap: 6
                        }}>
                          {isSinAsignar && <Icon name="alert" size={14} color={PALETTE.danger} />}
                          {isSinAsignar ? 'SIN ASIGNAR' : p.nombre}
                        </div>
                        <div style={{ fontSize: 12, color: PALETTE.slate500, marginTop: 2 }}>
                          {p.expedientes} expediente{p.expedientes === 1 ? '' : 's'} · {p.total} parte{p.total === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div style={{
                        textAlign: 'right',
                        background: '#fff', padding: '4px 10px', borderRadius: 10,
                        border: `1px solid ${PALETTE.slate100}`
                      }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: PALETTE.slate900, lineHeight: 1 }}>
                          {p.pctAvance}%
                        </div>
                        <div style={{ fontSize: 9, color: PALETTE.slate500, fontWeight: 600, letterSpacing: 0.5 }}>AVANCE</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                      <div style={{ background: '#fff', padding: '6px 10px', borderRadius: 8, border: `1px solid ${PALETTE.slate100}` }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: PALETTE.success, lineHeight: 1 }}>{p.completadas}</div>
                        <div style={{ fontSize: 9, color: PALETTE.slate500, fontWeight: 600 }}>OK</div>
                      </div>
                      {!isSinAsignar && (
                        <div style={{ background: '#fff', padding: '6px 10px', borderRadius: 8, border: `1px solid ${PALETTE.slate100}` }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: PALETTE.cyan, lineHeight: 1 }}>{p.enCurso}</div>
                          <div style={{ fontSize: 9, color: PALETTE.slate500, fontWeight: 600 }}>EN CURSO</div>
                        </div>
                      )}
                      {p.vencidas > 0 && (
                        <div style={{ background: '#fff', padding: '6px 10px', borderRadius: 8, border: `1px solid ${PALETTE.slate100}` }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: PALETTE.danger, lineHeight: 1 }}>{p.vencidas}</div>
                          <div style={{ fontSize: 9, color: PALETTE.slate500, fontWeight: 600 }}>VENCIDAS</div>
                        </div>
                      )}
                      {p.proximas > 0 && (
                        <div style={{ background: '#fff', padding: '6px 10px', borderRadius: 8, border: `1px solid ${PALETTE.slate100}` }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: PALETTE.warning, lineHeight: 1 }}>{p.proximas}</div>
                          <div style={{ fontSize: 9, color: PALETTE.slate500, fontWeight: 600 }}>≤7 DÍAS</div>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 10, height: 5, background: 'rgba(255,255,255,0.6)', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{
                        width: `${p.pctAvance}%`, height: '100%',
                        background: p.pctAvance >= 80 ? PALETTE.success : p.pctAvance >= 50 ? PALETTE.warning : PALETTE.danger,
                        borderRadius: 999
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )
        )}

        {tab === 'laboral' && (
          <TablaLaboral data={laboralMatriz} loading={loadingLaboral} />
        )}
      </div>

      {/* Acceso a tabla maestra — pantalla completa */}
      <div className="dac-fade dac-card" style={{
        background: 'linear-gradient(135deg, #fafbff 0%, #fff 100%)',
        borderRadius: 16,
        padding: '18px 20px',
        border: '1px solid rgba(199,210,254,0.6)',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(99,102,241,0.08)',
        marginBottom: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap'
      }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: PALETTE.slate900, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="table" size={18} color={PALETTE.primary} />
            Análisis administrativo
          </div>
          <div style={{ fontSize: 12, color: PALETTE.slate500, marginTop: 4, maxWidth: 520 }}>
            Rentabilidad, demoras y clientes que más convienen. Los datos brutos solo si los necesitas.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setTablaAdminOpen(true)}
          style={{
            border: 'none',
            background: `linear-gradient(135deg, ${PALETTE.primary} 0%, ${PALETTE.primaryDark} 100%)`,
            color: '#fff',
            borderRadius: 12,
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 8px 20px rgba(99,102,241,0.35)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 12px 28px rgba(99,102,241,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(99,102,241,0.35)';
          }}
        >
          <i className="fas fa-chart-pie" />
          Abrir análisis completo
          {datos.length > 0 && (
            <span style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '2px 8px',
              borderRadius: 999,
              fontSize: 12
            }}>
              {datos.length}
            </span>
          )}
        </button>
      </div>

      {(loading || loadingLaboral) && datos.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20,
          background: PALETTE.slate900, color: '#fff',
          padding: '10px 16px', borderRadius: 12,
          fontSize: 13, fontWeight: 600,
          boxShadow: '0 10px 30px rgba(15,23,42,0.3)',
          zIndex: 1000,
          display: 'flex', alignItems: 'center', gap: 10
        }}>
          <div style={{
            width: 14, height: 14, borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTopColor: '#fff',
            animation: 'dacSpin 700ms linear infinite'
          }} />
          Actualizando…
        </div>
      )}

      {detalleModalOpen && (
        <ModalCargaPersonalDetalle
          lista={cargaPersonal}
          isMobile={isMobile}
          onClose={() => setDetalleModalOpen(false)}
        />
      )}

      {tablaAdminOpen && (
        <VistaMaestraAdminFullscreen
          datos={datos}
          totales={totales}
          filtros={filtros}
          onExpedienteActualizado={handleExpedienteActualizado}
          onClose={() => setTablaAdminOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardAvanzadoClaude;
